import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { errMsg } from "../lib/api";
import { Mail, Camera, KeyRound, IdCard, Cake, CheckCircle2, ArrowLeft } from "lucide-react";

const inputCls = "w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-emerald-500/60";

export default function ResetPassword() {
  const [step, setStep] = useState("email"); // email -> verify -> password -> done
  const [email, setEmail] = useState("");
  const [session, setSession] = useState("");
  const [method, setMethod] = useState("");
  const [tgl, setTgl] = useState("");
  const [tempat, setTempat] = useState("");
  const [nik, setNik] = useState("");
  const [selfie, setSelfie] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const run = async (fn) => {
    setError(""); setLoading(true);
    try { await fn(); } catch (e) {
      setError(errMsg(e));
      if (e.response?.status === 429) setStep("email");
    } finally { setLoading(false); }
  };

  const begin = () => run(async () => {
    const r = await api.post("/auth/reset/begin", { email });
    setSession(r.data.session); setMethod(r.data.method); setStep("verify");
  });

  const onSelfie = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const scale = Math.min(1, 1024 / img.width);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        setSelfie(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const verify = () => run(async () => {
    const payload = method === "face" ? { session, tanggal_lahir: tgl, selfie } : { session, nik, tempat_lahir: tempat, tanggal_lahir: tgl };
    const r = await api.post(`/auth/reset/verify-${method}`, payload);
    setResetToken(r.data.reset_token); setStep("password");
  });

  const confirm = () => run(async () => {
    if (pw !== pw2) { setError("Konfirmasi password tidak sama"); return; }
    await api.post("/auth/reset/confirm", { reset_token: resetToken, new_password: pw });
    setStep("done");
  });

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="reset-page">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center gap-3 mb-8">
          <img src="/icons/icon-192.png" alt="Logo SPASI" className="w-20 h-20 rounded-3xl shadow-lg" />
          <div className="text-center">
            <h1 className="text-3xl font-extrabold tracking-[0.3em] text-slate-100">SPASI</h1>
            <p className="text-sm font-medium text-emerald-600 mt-1">Reset Password</p>
          </div>
        </div>

        <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-6 space-y-4 shadow-sm">
          {step === "email" && (
            <>
              <p className="text-sm text-slate-400">Masukkan email akun Anda. Verifikasi dilakukan sesuai jenis akun: <span className="text-slate-200">wajah + tanggal lahir</span> (karyawan) atau <span className="text-slate-200">NIK + tempat tanggal lahir</span> (konsumen).</p>
              <div>
                <span className="text-xs font-mono uppercase tracking-wider text-slate-400 block mb-1.5">Email</span>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reset-email-input" className={`${inputCls} pl-9`} placeholder="nama@perusahaan.id" />
                </div>
              </div>
              <button onClick={begin} disabled={loading || !email.trim()} data-testid="reset-begin-button" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg py-2.5 text-sm transition-colors disabled:opacity-50">
                {loading ? "Memproses..." : "Lanjut ke Verifikasi"}
              </button>
            </>
          )}

          {step === "verify" && method === "face" && (
            <>
              <p className="text-sm text-slate-400">Verifikasi identitas karyawan: isi tanggal lahir dan ambil foto selfie langsung. Wajah akan dicocokkan dengan foto profil Anda.</p>
              <div>
                <span className="text-xs font-mono uppercase tracking-wider text-slate-400 block mb-1.5">Tanggal Lahir</span>
                <div className="relative">
                  <Cake className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input type="date" value={tgl} onChange={(e) => setTgl(e.target.value)} data-testid="reset-tgl-input" className={`${inputCls} pl-9`} />
                </div>
              </div>
              <div>
                <span className="text-xs font-mono uppercase tracking-wider text-slate-400 block mb-1.5">Foto Selfie</span>
                <input ref={fileRef} type="file" accept="image/*" capture="user" onChange={onSelfie} className="hidden" data-testid="reset-selfie-input" />
                {selfie ? (
                  <img src={selfie} alt="Selfie verifikasi" className="w-full h-48 object-cover rounded-lg border border-slate-700" data-testid="reset-selfie-preview" />
                ) : (
                  <button type="button" onClick={() => fileRef.current?.click()} data-testid="reset-selfie-button" className="w-full h-32 border border-dashed border-slate-700 rounded-lg flex flex-col items-center justify-center gap-2 text-slate-400 hover:border-emerald-500/60 hover:text-emerald-500 transition-colors">
                    <Camera className="w-6 h-6" /><span className="text-xs">Ambil Foto Selfie</span>
                  </button>
                )}
                {selfie && <button type="button" onClick={() => fileRef.current?.click()} className="text-xs text-emerald-600 mt-1.5" data-testid="reset-selfie-retake">Ambil ulang foto</button>}
              </div>
              <button onClick={verify} disabled={loading || !tgl || !selfie} data-testid="reset-verify-button" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg py-2.5 text-sm transition-colors disabled:opacity-50">
                {loading ? "Memverifikasi wajah..." : "Verifikasi Identitas"}
              </button>
            </>
          )}

          {step === "verify" && method === "nik" && (
            <>
              <p className="text-sm text-slate-400">Verifikasi identitas konsumen: isi NIK dan tempat/tanggal lahir sesuai formulir booking Anda.</p>
              <div>
                <span className="text-xs font-mono uppercase tracking-wider text-slate-400 block mb-1.5">NIK (KTP)</span>
                <div className="relative">
                  <IdCard className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input inputMode="numeric" value={nik} onChange={(e) => setNik(e.target.value)} data-testid="reset-nik-input" className={`${inputCls} pl-9`} placeholder="16 digit NIK" />
                </div>
              </div>
              <div>
                <span className="text-xs font-mono uppercase tracking-wider text-slate-400 block mb-1.5">Tempat Lahir</span>
                <input value={tempat} onChange={(e) => setTempat(e.target.value)} data-testid="reset-tempat-input" className={inputCls} placeholder="Contoh: Stabat" />
              </div>
              <div>
                <span className="text-xs font-mono uppercase tracking-wider text-slate-400 block mb-1.5">Tanggal Lahir</span>
                <div className="relative">
                  <Cake className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input type="date" value={tgl} onChange={(e) => setTgl(e.target.value)} data-testid="reset-tgl-input" className={`${inputCls} pl-9`} />
                </div>
              </div>
              <button onClick={verify} disabled={loading || !nik.trim() || !tempat.trim() || !tgl} data-testid="reset-verify-button" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg py-2.5 text-sm transition-colors disabled:opacity-50">
                {loading ? "Memverifikasi..." : "Verifikasi Identitas"}
              </button>
            </>
          )}

          {step === "password" && (
            <>
              <p className="text-sm text-emerald-600 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Identitas terverifikasi. Buat password baru Anda.</p>
              <div>
                <span className="text-xs font-mono uppercase tracking-wider text-slate-400 block mb-1.5">Password Baru</span>
                <div className="relative">
                  <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-new-password-input" className={`${inputCls} pl-9`} placeholder="Minimal 6 karakter" />
                </div>
              </div>
              <div>
                <span className="text-xs font-mono uppercase tracking-wider text-slate-400 block mb-1.5">Ulangi Password Baru</span>
                <div className="relative">
                  <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-confirm-password-input" className={`${inputCls} pl-9`} placeholder="Ulangi password" />
                </div>
              </div>
              <button onClick={confirm} disabled={loading || pw.length < 6 || !pw2} data-testid="reset-confirm-button" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg py-2.5 text-sm transition-colors disabled:opacity-50">
                {loading ? "Menyimpan..." : "Simpan Password Baru"}
              </button>
            </>
          )}

          {step === "done" && (
            <div className="text-center space-y-4 py-4" data-testid="reset-success">
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
              <p className="text-slate-200 font-semibold">Password berhasil direset!</p>
              <p className="text-sm text-slate-400">Silakan masuk kembali menggunakan password baru Anda.</p>
              <Link to="/login" data-testid="reset-back-login" className="inline-block bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg px-6 py-2.5 text-sm transition-colors">Masuk ke SPASI</Link>
            </div>
          )}

          {error && <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/40 rounded-lg px-3 py-2" data-testid="reset-error">{error}</div>}

          {step !== "done" && (
            <div className="text-center pt-1">
              <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-emerald-500 transition-colors" data-testid="reset-cancel-link">
                <ArrowLeft className="w-3.5 h-3.5" /> Kembali ke halaman masuk
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
